import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createAdmin,
  deleteAdmin,
  updateAdmin,
} from "../../features/admins/adminsSlice";
import { hideConfirmationModal } from "../../features/confirmationModal/ConfirmationModalSlice";
import {
  addControlledAdmins,
  removeControlledAdmins,
} from "../../features/controlledHierarchy/controlledHierarchySlice";
import {
  addControlledTournaments,
  removeControlledTournaments,
} from "../../features/controlledTournaments/controlledTournamentsSlice";
import { deleteRegionImage } from "../../features/images/regionImages/regionImagesSlice";
import { deleteTournamentImage } from "../../features/images/tournamentImages/tournamentImagesSlice";
import { deleteUserImage } from "../../features/images/userImages/userImagesSlice";
import { deleteSquadImage } from "../../features/images/squadImages/squadImagesSlice";
import { deletePlayerFromTable } from "../../features/players/playersSlice";
import {
  deleteRegion,
  updateRegion,
} from "../../features/regions/regionsSlice";
import {
  deleteIngest,
  fetchTableIngestStatus,
} from "../../features/resultLogIngestSlice/resultLogIngestSlice";
import { createTable, deleteTable } from "../../features/tables/tableSlice";
import {
  deleteTournament,
  updateTournament,
} from "../../features/tournaments/tournamentsSlice";
import {
  deleteTournamentDetailsTemplate,
  updateTournamentDetailsTemplate,
} from "../../features/tournamentDetailsTemplates/tournamentDetailsTemplatesSlice";
import { deleteSquad, updateSquad } from "../../features/squads/squadsSlice";
import {
  adminBanUser,
  deleteUser,
  deleteUserGameId,
} from "../../features/users/usersSlice";
import {
  hideComment,
  deleteComment,
  dismissReports,
  restoreDeletedComment,
} from "../../features/tournamentCommentReports/tournamentCommentReportsSlice";
import {
  deleteCommentDirect,
  deleteAllUserComments,
  banUserFromCommenting,
} from "../../features/tournamentAllComments/tournamentAllCommentsSlice";
import Dialog from "../dialog/Dialog";
import DialogButton from "../dialog/DialogButton";
import AlertTriangleIcon from "../icons/AlertTriangleIcon";
import CheckCircleIcon from "../icons/CheckCircleIcon";
import HelpCircleIcon from "../icons/HelpCircleIcon";
import styles from "./confirmationModal.module.css";

// One look per kind of action, on every screen: banning a player and banning
// a squad are the same red "حظر", unbanning either is the same green. Actions
// that take something away are red, with the focus on "إلغاء" so a stray Enter
// doesn't confirm them.
const TONES = {
  danger: { Icon: AlertTriangleIcon, button: "danger" },
  success: { Icon: CheckCircleIcon, button: "primary" },
  info: { Icon: HelpCircleIcon, button: "primary" },
};
const KINDS = {
  delete: { tone: "danger", title: "تأكيد الحذف", confirm: "حذف" },
  remove: { tone: "danger", title: "تأكيد الإزالة", confirm: "إزالة" },
  ban: { tone: "danger", title: "تأكيد الحظر", confirm: "حظر" },
  hide: { tone: "danger", title: "تأكيد الإخفاء", confirm: "إخفاء" },
  unban: {
    tone: "success",
    title: "تأكيد إلغاء الحظر",
    confirm: "إلغاء الحظر",
  },
  show: { tone: "success", title: "تأكيد الإظهار", confirm: "إظهار" },
  restore: { tone: "success", title: "تأكيد الاستعادة", confirm: "استعادة" },
  add: { tone: "info", title: "تأكيد الإضافة", confirm: "إضافة" },
  create: { tone: "info", title: "تأكيد الإنشاء", confirm: "إنشاء" },
  update: { tone: "info", title: "تأكيد التعديل", confirm: "حفظ" },
  dismiss: {
    tone: "info",
    title: "تأكيد رفض البلاغات",
    confirm: "رفض البلاغات",
  },
  neutral: { tone: "info", title: "تأكيد الإجراء", confirm: "تأكيد" },
};
// The kind each action is. Toggles (banUser, updateSquad's ban) don't say
// which way they go, so their screens pass `confirmKind` ("ban" / "unban").
const KIND_BY_ACTION = {
  deleteAdmin: "delete",
  deleteTournament: "delete",
  deleteTournamentImage: "delete",
  deleteTournamentDetailsTemplate: "delete",
  deleteRegion: "delete",
  deleteRegionImage: "delete",
  deleteUser: "delete",
  deleteUserGameId: "delete",
  deleteUserImage: "delete",
  deleteSquad: "delete",
  deleteSquadImage: "delete",
  deleteTable: "delete",
  deletePlayerFromTable: "delete",
  deleteIngestFile: "delete",
  deleteComment: "delete",
  deleteCommentDirect: "delete",
  deleteAllUserComments: "delete",
  removeControlledAdmin: "remove",
  removeTournamentAdmins: "remove",
  banUserFromCommenting: "ban",
  hideComment: "hide",
  unhideComment: "show",
  restoreComment: "restore",
  addControlledAdmin: "add",
  addTournamentAdmins: "add",
  createAdmin: "create",
  createTable: "create",
  updateAdmin: "update",
  updateTournament: "update",
  updateTournamentDetailsTemplate: "update",
  updateRegion: "update",
  updateSquad: "update",
  dismissReports: "dismiss",
};

const ConfirmationModal = () => {
  const { isVisible, message, data, actionType, confirmKind } = useSelector(
    (state) => state.confirmationModal,
  );

  const dispatch = useDispatch();
  const confirmRef = useRef(null);
  const cancelRef = useRef(null);

  const kind =
    KINDS[confirmKind] || KINDS[KIND_BY_ACTION[actionType]] || KINDS.neutral;
  const tone = TONES[kind.tone];
  const destructive = kind.tone === "danger";

  const closeModalHandler = () => {
    dispatch(hideConfirmationModal());
  };

  const confirmModalHandler = () => {
    switch (actionType) {
      case "deleteAdmin":
        dispatch(deleteAdmin(data));
        closeModalHandler();
        break;
      case "createAdmin":
        dispatch(createAdmin(data));
        closeModalHandler();
        break;
      case "updateAdmin":
        dispatch(updateAdmin(data));
        closeModalHandler();
        break;
      case "deleteTournamentImage":
        dispatch(deleteTournamentImage(data));
        closeModalHandler();
        break;
      case "deleteTournament":
        dispatch(deleteTournament(data));
        closeModalHandler();
        break;
      case "updateTournament":
        dispatch(updateTournament(data));
        closeModalHandler();
        break;
      case "updateTournamentDetailsTemplate":
        dispatch(updateTournamentDetailsTemplate(data));
        closeModalHandler();
        break;
      case "deleteTournamentDetailsTemplate":
        dispatch(deleteTournamentDetailsTemplate(data));
        closeModalHandler();
        break;
      case "deleteRegionImage":
        dispatch(deleteRegionImage(data));
        closeModalHandler();
        break;
      case "deleteUser":
        dispatch(deleteUser(data));
        closeModalHandler();
        break;
      case "deleteUserGameId":
        dispatch(deleteUserGameId(data));
        closeModalHandler();
        break;
      case "deleteSquad":
        dispatch(deleteSquad(data));
        closeModalHandler();
        break;
      case "banUser":
        dispatch(adminBanUser(data));
        closeModalHandler();
        break;
      case "updateSquad":
        dispatch(updateSquad(data));
        closeModalHandler();
        break;
      case "deleteUserImage":
        dispatch(deleteUserImage(data));
        closeModalHandler();
        break;
      case "deleteSquadImage":
        dispatch(deleteSquadImage(data));
        closeModalHandler();
        break;
      case "createTable":
        dispatch(createTable(data));
        closeModalHandler();
        break;
      case "addControlledAdmin":
        dispatch(addControlledAdmins(data));
        closeModalHandler();
        break;
      case "removeControlledAdmin":
        dispatch(removeControlledAdmins(data));
        closeModalHandler();
        break;
      case "updateRegion":
        dispatch(updateRegion(data));
        closeModalHandler();
        break;
      case "deleteRegion":
        dispatch(deleteRegion(data));
        closeModalHandler();
        break;
      case "addTournamentAdmins":
        dispatch(addControlledTournaments(data));
        closeModalHandler();
        break;
      case "removeTournamentAdmins":
        dispatch(removeControlledTournaments(data));
        closeModalHandler();
        break;
      case "deleteTable":
        dispatch(deleteTable(data));
        closeModalHandler();
        break;
      case "deletePlayerFromTable":
        dispatch(deletePlayerFromTable(data));
        closeModalHandler();
        break;
      case "deleteIngestFile":
        dispatch(deleteIngest(data)).unwrap();
        dispatch(fetchTableIngestStatus({ tableId: data.tableId }));
        closeModalHandler();
        break;
      case "hideComment":
        dispatch(hideComment(data));
        closeModalHandler();
        break;
      case "deleteComment":
        dispatch(deleteComment(data.commentId));
        closeModalHandler();
        break;
      case "dismissReports":
        dispatch(dismissReports({ commentId: data.commentId }));
        closeModalHandler();
        break;
      case "unhideComment":
        dispatch(hideComment(data));
        closeModalHandler();
        break;
      case "restoreComment":
        dispatch(restoreDeletedComment(data.commentId));
        closeModalHandler();
        break;
      case "deleteCommentDirect":
        dispatch(deleteCommentDirect(data.commentId));
        closeModalHandler();
        break;
      case "deleteAllUserComments":
        dispatch(
          deleteAllUserComments({
            userId: data.userId,
            tournamentId: data.tournamentId,
          }),
        );
        closeModalHandler();
        break;
      case "banUserFromCommenting":
        dispatch(
          banUserFromCommenting({
            userId: data.userId,
            tournamentId: data.tournamentId,
          }),
        );
        closeModalHandler();
        break;
      default:
        closeModalHandler();
    }
  };

  if (!isVisible) return null;

  return (
    <Dialog
      role="alertdialog"
      size="sm"
      title={kind.title}
      icon={<tone.Icon />}
      tone={kind.tone}
      onClose={closeModalHandler}
      closeOnEscape
      closeOnBackdrop
      // Focus lands on the safe button.
      initialFocusRef={destructive ? cancelRef : confirmRef}
      footer={
        <>
          <DialogButton
            ref={cancelRef}
            variant="secondary"
            onClick={closeModalHandler}
          >
            إلغاء
          </DialogButton>
          <DialogButton
            ref={confirmRef}
            variant={tone.button}
            onClick={confirmModalHandler}
          >
            {kind.confirm}
          </DialogButton>
        </>
      }
    >
      <p className={styles.message}>{message}</p>
    </Dialog>
  );
};

export default ConfirmationModal;
